import { delay, takeEvery, takeLatest, select, call, all, put } from 'redux-saga/effects';
import { DELIVERYTYPE_SHIPPING, DELIVERYTYPE_STOREPICKUP } from '@krispsystems/constants';
import { get } from 'lodash';

import * as sagaCommon from '../sagasCommon';
import * as apiCommon from '../../services/apiCommon';
import {
  FETCH_LIST_SALE_TRANSACTION,
  FETCH_SALE_TRANSACTION_INVENTORY,
  GET_SALE_TRANSACTION,
  SAVE_SALE_TRANSACTION,
} from '../../constants/constant_saga_types';

import * as apiSaleTransaction from '../../services/sale_transaction/api_sale_transaction';
import { attachRecordListsToCustomForms } from '../../services/apiUtils';
import { getLocalDatetime } from '../../utils/local_date';
import { getParticularPreferenceSettings } from '../../common/selectors/preference';
import * as actions from '../../actions/actionCreatorsSaleTransaction';
import { getInventoryPagination } from '../../components/admin/sale_transaction/utils/sale_transaction_selector';

//Sale Transaction Listing
export function* fetchListSaleTransactionSaga(feathersApp) {
  yield takeLatest(
    FETCH_LIST_SALE_TRANSACTION,
    callFetchListSaleTransaction,
    feathersApp
  );
}

function* callFetchListSaleTransaction(feathersApp, action) {
  try {
    yield delay(250);
    const { silent, filters } = action;
    yield sagaCommon.showProgressListTable(silent);
    // Override values of range dates (dateFrom and dateTo) for use of local dates
    const { dateFrom, dateTo } = filters;
    filters.dateFromLocal = dateFrom ? getLocalDatetime(dateFrom) : undefined;
    filters.dateToLocal = dateTo ? getLocalDatetime(dateTo) : undefined;
    delete filters.dateFrom;
    delete filters.dateTo;

    const dataParams = {
      filters,
      list_type: 'sale_transaction',
    };
    const tableData = yield call(apiCommon.getDocs, feathersApp, dataParams);
    yield call(sagaCommon.doneListTable, {
      rows: tableData.data,
      total_rows: tableData.total,
    });
  } catch (e) {
    yield sagaCommon.showErrorListTable(e);
  }
}

//Sale Transaction ViewMode
export function* getSaleTransactionSaga(feathersApp) {
  yield takeEvery(GET_SALE_TRANSACTION, callGetSaleTransaction, feathersApp);
}

function* callGetSaleTransaction(feathersApp, action) {
  try {
    const { path, user } = yield select();
    const { id } = action;
    yield sagaCommon.showProgressDataById();

    if (id) {
      const data = yield call(
        apiSaleTransaction.fetchSaleTransactionById,
        feathersApp,
        { id, path, user, haveCustomFields: true }
      );

      // data.item_lines = orderBy(data.item_lines, ['created_datetime'], ['asc']);
      let loyaltyMvmntLines = [];
      if (data.customer && data.customer.loyalty_member) {
        const loyaltyMovement = yield call(apiCommon.getDoc, feathersApp, {
          id,
          docType: 'loyalty_movement/transaction',
        });
        if (loyaltyMovement) {
          loyaltyMvmntLines = loyaltyMovement.lines;
        }
      }

      for (let i = 0; i < data.item_lines.length; i++) {
        const itemLine = data.item_lines[i] || {};
        itemLine.custom_tabs = yield call(
          attachRecordListsToCustomForms,
          feathersApp,
          itemLine.custom_tabs
        );

        itemLine.loyalty_movement_line = loyaltyMvmntLines.find(
          (line) => itemLine.line_id === line.item_line_id
        );
        data.item_lines[i] = itemLine;
      }

      const orderManagementControlRecordsRes = yield call(apiCommon.getDocs, feathersApp, {
        filters: {
          sale_ids: [id]
        },
        list_type: 'oms/control_record/sale',
        methodType: 'post',
      });

      if (
        orderManagementControlRecordsRes.data &&
        orderManagementControlRecordsRes.data.length
      ) {
        const { value: isUseShippingIntegration = true } = yield select(getParticularPreferenceSettings,
          'oms_use_shipping_integrator_label',
          ['value'],
        ) || {};

        const orderManagementControlRecordBySale = get(orderManagementControlRecordsRes, 'data[0]') || {};
        const { oms_control_records, shipping_method } = orderManagementControlRecordBySale;
        const orderManagementControlRecordsToPrint =
          oms_control_records.reduce((groupedOmcrs, omcr) => {
            const {
              abandon,
              delivery_type,
              location_id,
              pack,
              pick,
              st_item_ship_line,
            } = omcr;

            if (abandon || (!pack && !pick)) {
              return groupedOmcrs;
            }

            const { shipping_label_url } = st_item_ship_line || {};
            const deliveryTypeCid = delivery_type.cid;

            let hasLabel;

            switch (deliveryTypeCid) {
              case DELIVERYTYPE_SHIPPING:
                hasLabel = isUseShippingIntegration ? Boolean(shipping_label_url) : true;
                break;

              case DELIVERYTYPE_STOREPICKUP:
                hasLabel = pack;
                break;

              default:
                break;
            }

            if (!groupedOmcrs[deliveryTypeCid]) {
              groupedOmcrs[deliveryTypeCid] = { locations: {} };
            }

            if (!groupedOmcrs[deliveryTypeCid]['locations'][location_id]) {
              groupedOmcrs[deliveryTypeCid]['locations'][location_id] = [];
            }

            if (!groupedOmcrs[deliveryTypeCid].pack) {
              groupedOmcrs[deliveryTypeCid].pack = 0;
            }

            if (!groupedOmcrs[deliveryTypeCid].label) {
              groupedOmcrs[deliveryTypeCid].label = 0;
            }

            if (hasLabel) {
              groupedOmcrs[deliveryTypeCid].label += 1;
            }

            omcr.shipping_method = shipping_method;
            groupedOmcrs[deliveryTypeCid].pack += 1;
            groupedOmcrs[deliveryTypeCid]['locations'][location_id].push(omcr);

            return groupedOmcrs;
          }, {});

          data.omcr_print = orderManagementControlRecordsToPrint;
      }

      const pagination = yield select(getInventoryPagination);
      const inventoryPayload = { ...pagination, id };
      yield put(actions.fetchSaleTransactionInventory(inventoryPayload));

      yield sagaCommon.doneDataById(data);
    }
  } catch (e) {
    yield sagaCommon.showErrorDataById(e, true);
  }
}

export function* saveSaleTransactionSaga(feathersApp) {
  yield takeEvery(SAVE_SALE_TRANSACTION, callSaveSaleTransaction, feathersApp);
}

function* callSaveSaleTransaction(feathersApp, action) {
  try {
    yield sagaCommon.showProgressDataById();

    const { tenant, user } = yield select();
    const { data } = action;
    const { item_lines } = data;

    const saveItemLineProcesses = item_lines.map(line => apiSaleTransaction.saveSaleTransactionItemLine(feathersApp, line));
    yield all(saveItemLineProcesses);

    const params = { tenant, user, data };
    const result = yield call(
      apiSaleTransaction.saveSaleTransaction,
      feathersApp,
      params
    );

    yield sagaCommon.doneDataById(result);
  } catch (e) {
    yield sagaCommon.showErrorSaveRecord(e, true);
  }
}

export function* fetchSaleTransactionInventorySaga(feathersApp) {
  yield takeEvery(
    FETCH_SALE_TRANSACTION_INVENTORY,
    fetchSaleTransactionInventory,
    feathersApp,
  );
}

function* fetchSaleTransactionInventory(feathersApp, action) {
  try {
    const { id, rows_per_page, requested_page } = action.payload;

    const query = apiCommon.parseFilters({ rows_per_page, requested_page });

    const data = yield call(
      apiCommon.getDoc,
      feathersApp,
      { id, docType: 'sale_transaction/inventory' },
      query,
    );

    const inventory = {
      ...action.payload,
      ...data,
      total_rows: data.total,
    };
    yield put(actions.setSaleTransactionInventory(inventory));
  } catch (e) {
    yield sagaCommon.showErrorDataById(e, true);
  }
}
